// @flow
// ─── DEV-ONLY: mock roaming options until the API provides real roaming keys ──
// Centralised module — used by Subscriptions, NextPlan, and AvailableOption sagas.

import config from '../config';

// ── Private helpers ──────────────────────────────────────────────────────────

const isRoamingMockEnabled = () =>
  config.areCheatCodesEnabled && config.displayRoamingPlanSliders;

const SUBSCRIPTION_MOCK_OPTIONS = [
  {
    name: 'ROA_DATA_ROA5_CREDIT',
    type: 'ROA5_DATA',
    characteristic: { unit: 'VOLUME', value: '10485760' },
    characteristics: [{ name: 'ROA_DATA_ROA5_CREDIT', unit: 'VOLUME', value: '10485760' }],
  },
  {
    name: 'IDD_VOICE_D_CREDIT',
    type: 'TO_ZONED_VOICE',
    characteristic: { unit: 'DURATION', value: '3600' },
    characteristics: [{ name: 'IDD_VOICE_D_CREDIT', unit: 'DURATION', value: '3600' }],
  },
];

const hasSubscriptionRoaming = (options) =>
  options.some(opt => opt.name && (opt.name.includes('ROA') || opt.name.includes('IDD')));

const injectIntoSinglePlan = (plan) => {
  const options = plan.options || [];
  if (hasSubscriptionRoaming(options)) return plan;
  return { ...plan, options: [...options, ...SUBSCRIPTION_MOCK_OPTIONS] };
};

const CATALOG_MOCK_OPTIONS = {
  ROA4_DATA: [{
    ouid: 'mock_roa4_data',
    name: 'KAIZEN_OPTDATA_ROA4_5GB',
    type: 'ROA4_DATA',
    relationships: [],
    periodicity: 'MONTH',
    characteristic: { unit: 'VOLUME', value: '5242880' },
    characteristics: [{ name: 'ROA_DATA_ROA4_CREDIT', unit: 'VOLUME', value: '5242880' }],
    price: { taxIncludedAmount: 500 },
  }],
  ROA5_DATA: [{
    ouid: 'mock_roa5_data',
    name: 'KAIZEN_OPTDATA_ROA5_10GB',
    type: 'ROA5_DATA',
    relationships: [],
    periodicity: 'MONTH',
    characteristic: { unit: 'VOLUME', value: '10485760' },
    characteristics: [{ name: 'ROA_DATA_ROA5_CREDIT', unit: 'VOLUME', value: '10485760' }],
    price: { taxIncludedAmount: 1000 },
  }],
  ROA6_DATA: [{
    ouid: 'mock_roa6_data',
    name: 'KAIZEN_OPTDATA_ROA6_15GB',
    type: 'ROA6_DATA',
    relationships: [],
    periodicity: 'MONTH',
    characteristic: { unit: 'VOLUME', value: '15728640' },
    characteristics: [{ name: 'ROA_DATA_ROA6_CREDIT', unit: 'VOLUME', value: '15728640' }],
    price: { taxIncludedAmount: 1500 },
  }],
  TO_ZONED_VOICE: [
    {
      ouid: 'mock_idd_30',
      name: 'KAIZEN_OPT_IDD_ZONED_30',
      type: 'TO_ZONED_VOICE',
      relationships: [],
      periodicity: 'MONTH',
      characteristic: { unit: 'DURATION', value: '1800' },
      characteristics: [{ name: 'IDD_VOICE_D_CREDIT', unit: 'DURATION', value: '1800' }],
      price: { taxIncludedAmount: 300 },
    },
    {
      ouid: 'mock_idd_60',
      name: 'KAIZEN_OPT_IDD_ZONED_60',
      type: 'TO_ZONED_VOICE',
      relationships: [],
      periodicity: 'MONTH',
      characteristic: { unit: 'DURATION', value: '3600' },
      characteristics: [{ name: 'IDD_VOICE_D_CREDIT', unit: 'DURATION', value: '3600' }],
      price: { taxIncludedAmount: 600 },
    },
    {
      ouid: 'mock_idd_120',
      name: 'KAIZEN_OPT_IDD_ZONED_120',
      type: 'TO_ZONED_VOICE',
      relationships: [],
      periodicity: 'MONTH',
      characteristic: { unit: 'DURATION', value: '7200' },
      characteristics: [{ name: 'IDD_VOICE_D_CREDIT', unit: 'DURATION', value: '7200' }],
      price: { taxIncludedAmount: 900 },
    },
  ],
};

const hasCatalogRoaming = (creditableOptions) =>
  Object.keys(creditableOptions || {}).some(
    key => (config.roamingServiceSpecs || []).includes(key),
  );

// ── Public exports ───────────────────────────────────────────────────────────

/**
 * Injects mock roaming options into each subscription in the array.
 * @param {Array} subscriptions
 * @param {boolean} extraGuard — typically `config.enableRoamingOptionsMockInCurrentSubscription`
 */
export const injectMockRoamingSubscriptionOptions = (subscriptions, extraGuard = false) => {
  if (!extraGuard || !isRoamingMockEnabled()) return subscriptions;
  return subscriptions.map(injectIntoSinglePlan);
};

/**
 * Injects mock roaming options into a single nextPlan object (null-safe).
 */
export const injectMockRoamingNextPlanOptions = (nextPlan) => {
  if (!nextPlan || !isRoamingMockEnabled()) return nextPlan;
  return injectIntoSinglePlan(nextPlan);
};

/**
 * Injects mock roaming catalog options into the `creditableOptions` dict.
 */
export const injectMockRoamingCatalogOptions = (options) => {
  if (!isRoamingMockEnabled()) return options;
  if (hasCatalogRoaming(options.creditableOptions)) return options;
  return {
    ...options,
    creditableOptions: {
      ...options.creditableOptions,
      ...CATALOG_MOCK_OPTIONS,
    },
  };
};